import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Card, Grid, makeStyles, SvgIcon, Typography } from '@material-ui/core';

import { DownloadIcon, ExcelTemplateIcon } from 'components/Icons';
import { colors, fonts } from 'theme';
import YoutubeEmbed from 'components/YoutubeEmbed';
import PdfTemplateIcon from 'components/Icons/PdfTemplateIcon';

const useStyles = makeStyles({
  grid: {
    alignItems: 'center',
    display: 'flex',
    marginTop: '1em',
    marginBottom: '2em',
  },
  link: {
    textDecoration: 'none',
    color: 'inherit',
  },
  templateIcon: {
    color: colors.greyLighten20,
    width: '60px',
    height: '68px',
  },
  cardStyle: {
    minHeight: '189px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  emptyCard: {
    minHeight: '189px',
  },
  videoCard: {
    minHeight: '189px',
    position: 'relative',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontFamily: fonts.titles,
    maxWidth: '200px',
    marginBottom: '34px',
  },
  cardNumber: {
    display: 'flex',
    fontSize: 44,
    color: colors.primary,
    marginRight: '10px',
  },
  cardText: {
    display: 'flex',
    fontSize: 14,
  },
  cardDownload: {
    display: 'flex',
    fontFamily: fonts.primary,
    justifyContent: 'center',
    alignContent: 'center',
    marginBottom: '27px',
  },
  downloadIcon: {
    marginRight: '10px',
    height: '14px',
  },
  title: {
    fontFamily: fonts.titles,
    textTransform: 'uppercase',
    fontSize: '16px',
    display: 'flex',
    '&:after': {
      content: '""',
      marginTop: 8,
      marginLeft: 12,
      backgroundColor: colors.primary,
      height: 1,
      flexGrow: 1,
    },
  },
});

interface DownloadCardProps {
  num: number;
  text: string;
  icon: React.MemoExoticComponent<(props: React.SVGProps<SVGSVGElement>) => JSX.Element>;
}

enum Template {
  ReceptionImport = '/import_receptions.csv',
  NoticeReceptionImport = '/e-tem_Notice_des_imports_de_receptions.pdf',
  NoticeAdministrateurImport = '/e-tem_Notice_Administrateur.pdf',
  NoticeBilanImport = '/e-tem_Notice_Bilans.pdf',
  NoticeExploitation = '/e-tem_Notice_Exploitation.pdf',
  NoticeClotureImport = '/e-tem_Notice_Clotures.pdf',
  NoticeReferentielImport = '/e-tem_Notice_Referentiel.pdf',
  CharacterisationImport = '/import_caracterisations.xlsx',
  NoticeCaracterisationImport = '/e-tem_Notice_des_imports_des_caractérisations.pdf',
}

const TEMPLATES_FOLDER = '/templates';

const DownloadCard: React.FC<DownloadCardProps> = ({ num, text, icon }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <>
      <div className={classes.cardContent}>
        <div className={classes.cardNumber}>{num}</div>
        <div className={classes.cardText}>{text}</div>
        <SvgIcon component={icon} className={classes.templateIcon} />
      </div>
      <div className={classes.cardDownload}>
        <DownloadIcon className={classes.downloadIcon} />
        {t('resources.download').toUpperCase()}
      </div>
    </>
  );
};

const DocumentList: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Box py={5} px={3} maxWidth="80%">
      <Typography variant="h2" className={classes.title} color="primary">
        {t('resources.documents')}
      </Typography>
      <Grid container spacing={3} className={classes.grid}>
        <Grid item xs={4}>
          <Card className={classes.cardStyle}>
            <a download href={`${TEMPLATES_FOLDER}${Template.ReceptionImport}`} className={classes.link}>
              <DownloadCard num={1} text={t('resources.receptionImport')} icon={ExcelTemplateIcon} />
            </a>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card className={classes.cardStyle}>
            <a download href={`${TEMPLATES_FOLDER}${Template.NoticeReceptionImport}`} className={classes.link}>
              <DownloadCard num={2} text={t('resources.noticeReceptionImport')} icon={PdfTemplateIcon} />
            </a>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card className={classes.cardStyle}>
            <a download href={`${TEMPLATES_FOLDER}${Template.CharacterisationImport}`} className={classes.link}>
              <DownloadCard num={3} text={t('resources.caracterisationImport')} icon={ExcelTemplateIcon} />
            </a>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={3} className={classes.grid}>
        <Grid item xs={4}>
          <Card className={classes.cardStyle}>
            <a download href={`${TEMPLATES_FOLDER}${Template.NoticeCaracterisationImport}`} className={classes.link}>
              <DownloadCard num={4} text={t('resources.noticeCaracterisation')} icon={PdfTemplateIcon} />
            </a>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card className={classes.cardStyle}>
            <a download href={`${TEMPLATES_FOLDER}${Template.NoticeAdministrateurImport}`} className={classes.link}>
              <DownloadCard num={5} text={t('resources.noticeAdministrateurImport')} icon={PdfTemplateIcon} />
            </a>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card className={classes.cardStyle}>
            <a download href={`${TEMPLATES_FOLDER}${Template.NoticeClotureImport}`} className={classes.link}>
              <DownloadCard num={6} text={t('resources.noticeClotureImport')} icon={PdfTemplateIcon} />
            </a>
          </Card>
        </Grid>
        <Grid container spacing={3} className={classes.grid}>
          <Grid item xs={4}>
            <Card className={classes.cardStyle}>
              <a download href={`${TEMPLATES_FOLDER}${Template.NoticeReferentielImport}`} className={classes.link}>
                <DownloadCard num={7} text={t('resources.noticeReferentielImport')} icon={PdfTemplateIcon} />
              </a>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card className={classes.cardStyle}>
              <a download href={`${TEMPLATES_FOLDER}${Template.NoticeBilanImport}`} className={classes.link}>
                <DownloadCard num={8} text={t('resources.noticeBilanImport')} icon={PdfTemplateIcon} />
              </a>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card className={classes.cardStyle}>
              <a download href={`${TEMPLATES_FOLDER}${Template.NoticeExploitation}`} className={classes.link}>
                <DownloadCard num={9} text={t('resources.noticeExploitation')} icon={PdfTemplateIcon} />
              </a>
            </Card>
          </Grid>
        </Grid>
      </Grid>
      <Typography variant="h2" className={classes.title} color="primary">
        {t('resources.tutorials')}
      </Typography>
      <Grid container spacing={3} className={classes.grid}>
        <Grid item xs={4}>
          <Card className={classes.videoCard}>
            <YoutubeEmbed videoId="FAkxHyCRHWI" title={t('receptions.entityName')} />
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card className={classes.videoCard}>
            <YoutubeEmbed videoId="-lU6SnKOGag" title={t('characterizations.entityName')} />
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card className={classes.videoCard}>
            <YoutubeEmbed videoId="XWTe2Qcmamk" title={t('productions.entityName')} />
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card className={classes.videoCard}>
            <YoutubeEmbed videoId="DluHxDWRsrw" title={t('expeditions.entityName')} />
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card className={classes.videoCard}>
            <YoutubeEmbed videoId="_EaoKZqFkdA" title={t('closures.entityName')} />
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card className={classes.emptyCard} />
        </Grid>
      </Grid>
    </Box>
  );
};
export default DocumentList;
